import { useState, useEffect, useCallback, useRef } from "react";
import {
  Plus, Trash2, Save, Copy, FolderPlus, ChevronRight,
  GripVertical, X, Search, Edit2, ArrowLeft, Folder,
  ChevronDown, Check, Users, AlertCircle
} from "lucide-react";
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragOverlay
} from "@dnd-kit/core";
import {
  SortableContext, useSortable, verticalListSortingStrategy, arrayMove
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const API = process.env.REACT_APP_BACKEND_URL;
const DOELEN = ["Vetverlies", "Spieropbouw", "Onderhoud", "Powerlifting", "Conditie", "Kracht"];
const NIVEAUS = ["Beginner", "Gevorderd", "Advanced"];
const RUST_OPTIES = ["30 sec", "45 sec", "60 sec", "90 sec", "2:00", "2:30", "3:00", "4:00", "5:00"];
const SPIERGROEPEN = ["Benen", "Billen", "Borst", "Rug", "Schouders", "Biceps", "Triceps", "Core", "Full Body", "Kuiten", "Anders"];

// ─── Helpers ─────────────────────────────────────────────────────────────────
function uid() { return Math.random().toString(36).slice(2, 9); }

// ─── Sortable Exercise Row ────────────────────────────────────────────────────
function SortableExerciseRow({ ex, onUpdate, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: ex.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };

  return (
    <div ref={setNodeRef} style={style} className="bg-[#141414] border border-white/6 rounded-xl overflow-hidden mb-2">
      {/* Top row */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-white/5">
        <button {...attributes} {...listeners} className="text-zinc-600 hover:text-zinc-400 cursor-grab active:cursor-grabbing p-0.5" data-testid="drag-handle">
          <GripVertical size={15} />
        </button>
        <div className="flex-1 font-semibold text-white text-sm truncate">{ex.naam || <span className="text-zinc-600 italic">Oefening</span>}</div>
        <span className="text-zinc-600 text-xs">{ex.spiergroep}</span>
        <button onClick={onDelete} className="text-zinc-600 hover:text-red-400 transition-colors p-1">
          <Trash2 size={13} />
        </button>
      </div>
      {/* Details grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 p-3">
        <label className="flex flex-col gap-1">
          <span className="text-[10px] text-zinc-600 uppercase font-bold tracking-wider">Sets</span>
          <input type="number" min="1" max="20" value={ex.sets}
            onChange={e => onUpdate({ ...ex, sets: parseInt(e.target.value) || 1 })}
            className="w-full bg-[#111] border border-white/8 rounded-xl px-4 py-3 text-white text-sm placeholder:text-zinc-600 focus:border-[#C9A84C]/50 focus:outline-none transition-colors" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[10px] text-zinc-600 uppercase font-bold tracking-wider">Reps</span>
          <input type="text" value={ex.reps} placeholder="bijv. 8-10"
            onChange={e => onUpdate({ ...ex, reps: e.target.value })}
            className="w-full bg-[#111] border border-white/8 rounded-xl px-4 py-3 text-white text-sm placeholder:text-zinc-600 focus:border-[#C9A84C]/50 focus:outline-none transition-colors" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[10px] text-zinc-600 uppercase font-bold tracking-wider">Rusttijd</span>
          <select value={ex.rust} onChange={e => onUpdate({ ...ex, rust: e.target.value })} className="w-full bg-[#111] border border-white/8 rounded-xl px-4 py-3 text-white text-sm placeholder:text-zinc-600 focus:border-[#C9A84C]/50 focus:outline-none transition-colors">
            {RUST_OPTIES.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[10px] text-zinc-600 uppercase font-bold tracking-wider">Tempo</span>
          <input type="text" value={ex.tempo} placeholder="bijv. 3-1-1-0"
            onChange={e => onUpdate({ ...ex, tempo: e.target.value })}
            className="w-full bg-[#111] border border-white/8 rounded-xl px-4 py-3 text-white text-sm placeholder:text-zinc-600 focus:border-[#C9A84C]/50 focus:outline-none transition-colors" />
        </label>
        <label className="col-span-2 flex flex-col gap-1">
          <span className="text-[10px] text-zinc-600 uppercase font-bold tracking-wider">Gewicht (optioneel)</span>
          <input type="text" value={ex.gewicht} placeholder="bijv. 60 kg of licht"
            onChange={e => onUpdate({ ...ex, gewicht: e.target.value })}
            className="w-full bg-[#111] border border-white/8 rounded-xl px-4 py-3 text-white text-sm placeholder:text-zinc-600 focus:border-[#C9A84C]/50 focus:outline-none transition-colors" />
        </label>
        <label className="col-span-2 flex flex-col gap-1">
          <span className="text-[10px] text-zinc-600 uppercase font-bold tracking-wider">Notities / coaching cues</span>
          <input type="text" value={ex.notities} placeholder="bijv. Hou je rug recht"
            onChange={e => onUpdate({ ...ex, notities: e.target.value })}
            className="w-full bg-[#111] border border-white/8 rounded-xl px-4 py-3 text-white text-sm placeholder:text-zinc-600 focus:border-[#C9A84C]/50 focus:outline-none transition-colors" />
        </label>
      </div>
    </div>
  );
}

// ─── Exercise Picker Modal ────────────────────────────────────────────────────
function ExercisePickerModal({ onSelect, onClose, token }) {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("");
  const [exercises, setExercises] = useState([]);
  const [newForm, setNewForm] = useState({ naam: "", spiergroep: "Anders", type: "", apparaat: "", uitvoeringstip: "" });
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/exercises`).then(r => r.json()).then(d => setExercises(d.exercises || []));
  }, []);

  const filtered = exercises.filter(e =>
    (!query || e.naam.toLowerCase().includes(query.toLowerCase())) &&
    (!filter || e.spiergroep === filter)
  );

  const handleAdd = async () => {
    if (!newForm.naam.trim()) return;
    setAdding(true);
    await fetch(`${API}/api/admin/exercises`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(newForm)
    });
    const newEx = { ...newForm };
    setExercises(p => [...p, newEx]);
    onSelect(newEx);
    onClose();
    setAdding(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#111] border border-white/10 rounded-2xl w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/8">
          <h3 className="text-white font-bold text-sm">Oefening selecteren</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X size={16} /></button>
        </div>
        {/* Search + filter */}
        <div className="px-4 pt-3 pb-2 space-y-2">
          <div className="flex items-center gap-2 bg-[#1a1a1a] border border-white/8 rounded-xl px-3 py-2">
            <Search size={13} className="text-zinc-500 shrink-0" />
            <input autoFocus value={query} onChange={e => setQuery(e.target.value)}
              placeholder="Zoek oefening..." className="bg-transparent flex-1 text-sm text-white outline-none placeholder-zinc-600" />
          </div>
          <div className="flex gap-1.5 flex-wrap">
            <button onClick={() => setFilter("")} className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-colors ${!filter ? "bg-[#C9A84C] text-black" : "bg-[#1a1a1a] text-zinc-400 hover:text-white"}`}>Alles</button>
            {SPIERGROEPEN.map(s => (
              <button key={s} onClick={() => setFilter(filter === s ? "" : s)}
                className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-colors ${filter === s ? "bg-[#C9A84C] text-black" : "bg-[#1a1a1a] text-zinc-400 hover:text-white"}`}>{s}</button>
            ))}
          </div>
        </div>
        {/* Exercise list */}
        <div className="flex-1 overflow-y-auto px-4 pb-2">
          {filtered.map((ex, i) => (
            <button key={i} onClick={() => onSelect(ex)}
              className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl hover:bg-[#1a1a1a] transition-colors text-left group">
              <div>
                <span className="text-white text-sm font-medium group-hover:text-[#C9A84C] transition-colors block">{ex.naam}</span>
                {(ex.type || ex.apparaat) && (
                  <span className="text-zinc-600 text-xs">{[ex.type, ex.apparaat].filter(Boolean).join(" · ")}</span>
                )}
              </div>
              <span className="text-zinc-600 text-xs shrink-0 ml-2">{ex.spiergroep}</span>
            </button>
          ))}
          {filtered.length === 0 && <p className="text-zinc-600 text-sm text-center py-6">Geen oefeningen gevonden</p>}
        </div>
        {/* Add custom */}
        <div className="px-4 py-3 border-t border-white/8 space-y-2">
          <p className="text-zinc-500 text-xs font-semibold uppercase tracking-wider">Nieuwe oefening toevoegen</p>
          <div className="flex gap-2">
            <input value={newForm.naam} onChange={e => setNewForm(f => ({ ...f, naam: e.target.value }))}
              placeholder="Naam oefening"
              className="flex-1 bg-[#111] text-white border border-white/10 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#C9A84C] placeholder-zinc-600"
              onKeyDown={e => e.key === "Enter" && handleAdd()} />
            <select value={newForm.spiergroep} onChange={e => setNewForm(f => ({ ...f, spiergroep: e.target.value }))}
              className="w-28 bg-[#111] text-white border border-white/10 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#C9A84C] appearance-none">
              {SPIERGROEPEN.map(s => <option key={s} className="bg-[#111] text-white">{s}</option>)}
            </select>
          </div>
          <div className="flex gap-2">
            <select value={newForm.type} onChange={e => setNewForm(f => ({ ...f, type: e.target.value }))}
              className="flex-1 bg-[#111] text-white border border-white/10 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#C9A84C] appearance-none">
              <option value="" className="bg-[#111]">Type (opt.)</option>
              <option className="bg-[#111]">Compound</option>
              <option className="bg-[#111]">Isolatie</option>
            </select>
            <select value={newForm.apparaat} onChange={e => setNewForm(f => ({ ...f, apparaat: e.target.value }))}
              className="flex-1 bg-[#111] text-white border border-white/10 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#C9A84C] appearance-none">
              <option value="" className="bg-[#111]">Apparaat (opt.)</option>
              <option className="bg-[#111]">Barbell</option><option className="bg-[#111]">Dumbbell</option><option className="bg-[#111]">Machine</option>
              <option className="bg-[#111]">Kabel</option><option className="bg-[#111]">Bodyweight</option><option className="bg-[#111]">Anders</option>
            </select>
          </div>
          <div className="flex gap-2">
            <input value={newForm.uitvoeringstip} onChange={e => setNewForm(f => ({ ...f, uitvoeringstip: e.target.value }))}
              placeholder="Uitvoeringstip (opt.)"
              className="flex-1 bg-[#111] text-white border border-white/10 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#C9A84C] placeholder-zinc-600" />
            <button onClick={handleAdd} disabled={adding || !newForm.naam.trim()}
              className="px-3 py-2 bg-[#C9A84C] text-black rounded-xl text-xs font-bold disabled:opacity-50">
              <Plus size={13} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Assign Modal ─────────────────────────────────────────────────────────────
function AssignModal({ schemaId, token, onClose }) {
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState({});
  const [done, setDone] = useState({});

  useEffect(() => {
    fetch(`${API}/api/admin/users`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => setUsers(d.users || []));
  }, [token]);

  const assign = async (userId) => {
    setSaving(p => ({ ...p, [userId]: true }));
    await fetch(`${API}/api/admin/custom-schemas/${schemaId}/assign`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ user_id: userId })
    });
    setSaving(p => ({ ...p, [userId]: false }));
    setDone(p => ({ ...p, [userId]: true }));
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#111] border border-white/10 rounded-2xl w-full max-w-sm overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/8">
          <h3 className="text-white font-bold text-sm">Toewijzen aan klant</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X size={16} /></button>
        </div>
        <div className="max-h-72 overflow-y-auto p-3">
          {users.filter(u => u.role !== "admin").map(u => (
            <div key={u.id} className="flex items-center justify-between px-3 py-2.5 rounded-xl hover:bg-[#1a1a1a]">
              <div>
                <p className="text-white text-sm font-semibold">{u.name}</p>
                <p className="text-zinc-600 text-xs">{u.email}</p>
              </div>
              <button onClick={() => assign(u.id)} disabled={saving[u.id]}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${done[u.id] ? "bg-green-500/20 text-green-400" : "bg-[#C9A84C] text-black hover:bg-[#D4AF37]"}`}>
                {done[u.id] ? <Check size={12} /> : saving[u.id] ? "..." : "Toewijzen"}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Training Dag Editor ──────────────────────────────────────────────────────
function DagEditor({ dag, onChange, token }) {
  const [showPicker, setShowPicker] = useState(false);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = dag.oefeningen.findIndex(e => e.id === active.id);
    const newIdx = dag.oefeningen.findIndex(e => e.id === over.id);
    onChange({ ...dag, oefeningen: arrayMove(dag.oefeningen, oldIdx, newIdx) });
  };

  const addExercise = (ex) => {
    const newEx = { id: uid(), naam: ex.naam, spiergroep: ex.spiergroep || "", sets: 3, reps: "10", gewicht: "", rust: "90 sec", tempo: "", notities: ex.uitvoeringstip || "", volgorde: dag.oefeningen.length };
    onChange({ ...dag, oefeningen: [...dag.oefeningen, newEx] });
    setShowPicker(false);
  };

  const updateEx = (idx, updated) => {
    const next = [...dag.oefeningen];
    next[idx] = updated;
    onChange({ ...dag, oefeningen: next });
  };

  const deleteEx = (idx) => {
    onChange({ ...dag, oefeningen: dag.oefeningen.filter((_, i) => i !== idx) });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <input value={dag.dag_naam} onChange={e => onChange({ ...dag, dag_naam: e.target.value })}
            className="bg-transparent text-white font-bold text-sm border-b border-white/10 focus:border-[#C9A84C] outline-none pb-0.5 w-40" />
        </div>
        <span className="text-zinc-600 text-xs">{dag.oefeningen.length} oef.</span>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={dag.oefeningen.map(e => e.id)} strategy={verticalListSortingStrategy}>
          {dag.oefeningen.map((ex, i) => (
            <SortableExerciseRow key={ex.id} ex={ex} onUpdate={(u) => updateEx(i, u)} onDelete={() => deleteEx(i)} />
          ))}
        </SortableContext>
      </DndContext>

      <button onClick={() => setShowPicker(true)} data-testid="add-exercise-btn"
        className="w-full py-2.5 flex items-center justify-center gap-2 text-sm font-semibold text-zinc-400 hover:text-[#C9A84C] border border-dashed border-white/10 hover:border-[#C9A84C]/40 rounded-xl transition-all">
        <Plus size={14} /> Oefening toevoegen
      </button>

      {showPicker && <ExercisePickerModal onSelect={addExercise} onClose={() => setShowPicker(false)} token={token} />}
    </div>
  );
}

// ─── Schema Editor Form ───────────────────────────────────────────────────────
function SchemaEditorForm({ schema, folders, onSave, onCancel, token }) {
  const [form, setForm] = useState(schema || {
    naam: "", doel: "Spieropbouw", niveau: "Beginner", trainingsdagen: 3,
    beschrijving: "", folder_id: null, weken: 6, dagen: []
  });
  const [activeDag, setActiveDag] = useState(0);
  const [saving, setSaving] = useState(false);
  const [assignModal, setAssignModal] = useState(false);

  // Sync training days with count — computed inside callback to avoid React 18 Strict Mode double-invoke
  useEffect(() => {
    const count = Math.max(1, Math.min(7, form.trainingsdagen));
    setForm(f => {
      if (f.dagen.length === count) return f;
      if (f.dagen.length < count) {
        const extra = Array.from({ length: count - f.dagen.length }, (_, i) => ({
          dag_naam: `Dag ${f.dagen.length + i + 1}`,
          dag_nummer: f.dagen.length + i + 1,
          oefeningen: []
        }));
        return { ...f, dagen: [...f.dagen, ...extra] };
      }
      return { ...f, dagen: f.dagen.slice(0, count) };
    });
  }, [form.trainingsdagen]);

  const handleSave = async () => {
    if (!form.naam.trim()) return;
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  const updateDag = (idx, updated) => {
    const next = [...form.dagen];
    next[idx] = updated;
    setForm(f => ({ ...f, dagen: next }));
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-white/6 shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={onCancel} className="w-8 h-8 rounded-xl bg-[#1a1a1a] flex items-center justify-center text-zinc-400 hover:text-white">
            <ArrowLeft size={15} />
          </button>
          <div>
            <p className="text-zinc-500 text-xs">Schema Builder</p>
            <h2 className="text-white font-bold text-sm">{form.naam || "Nieuw schema"}</h2>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {schema?.id && (
            <button onClick={() => setAssignModal(true)} data-testid="assign-schema-btn"
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 text-xs font-bold transition-colors">
              <Users size={12} /> Toewijzen
            </button>
          )}
          <button onClick={handleSave} disabled={saving || !form.naam.trim()} data-testid="save-schema-btn"
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#C9A84C] to-[#D4AF37] text-black font-bold text-xs rounded-xl disabled:opacity-50">
            <Save size={13} /> {saving ? "Opslaan..." : "Opslaan"}
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
        {/* Basic info */}
        <div className="bg-[#0f0f0f] border border-white/6 rounded-2xl p-5 space-y-4">
          <h3 className="text-zinc-300 text-xs font-bold uppercase tracking-widest">Schema-informatie</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="flex flex-col gap-1.5">
              <span className="text-zinc-500 text-xs font-bold uppercase tracking-wider">Naam *</span>
              <input value={form.naam} onChange={e => setForm(f => ({ ...f, naam: e.target.value }))}
                placeholder="bijv. Spieropbouw Beginner 3x" className="w-full bg-[#111] border border-white/8 rounded-xl px-4 py-3 text-white text-sm placeholder:text-zinc-600 focus:border-[#C9A84C]/50 focus:outline-none transition-colors" data-testid="schema-naam-input" />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-zinc-500 text-xs font-bold uppercase tracking-wider">Map</span>
              <select value={form.folder_id || ""} onChange={e => setForm(f => ({ ...f, folder_id: e.target.value || null }))} className="w-full bg-[#111] border border-white/8 rounded-xl px-4 py-3 text-white text-sm placeholder:text-zinc-600 focus:border-[#C9A84C]/50 focus:outline-none transition-colors">
                <option value="">Geen map</option>
                {folders.map(f => <option key={f.id} value={f.id}>{f.naam}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-zinc-500 text-xs font-bold uppercase tracking-wider">Doel</span>
              <select value={form.doel} onChange={e => setForm(f => ({ ...f, doel: e.target.value }))} className="w-full bg-[#111] border border-white/8 rounded-xl px-4 py-3 text-white text-sm placeholder:text-zinc-600 focus:border-[#C9A84C]/50 focus:outline-none transition-colors">
                {DOELEN.map(d => <option key={d}>{d}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-zinc-500 text-xs font-bold uppercase tracking-wider">Niveau</span>
              <select value={form.niveau} onChange={e => setForm(f => ({ ...f, niveau: e.target.value }))} className="w-full bg-[#111] border border-white/8 rounded-xl px-4 py-3 text-white text-sm placeholder:text-zinc-600 focus:border-[#C9A84C]/50 focus:outline-none transition-colors">
                {NIVEAUS.map(n => <option key={n}>{n}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-zinc-500 text-xs font-bold uppercase tracking-wider">Trainingsdagen / week</span>
              <input type="number" min="1" max="7" value={form.trainingsdagen}
                onChange={e => setForm(f => ({ ...f, trainingsdagen: parseInt(e.target.value) || 1 }))}
                className="w-full bg-[#111] border border-white/8 rounded-xl px-4 py-3 text-white text-sm placeholder:text-zinc-600 focus:border-[#C9A84C]/50 focus:outline-none transition-colors" data-testid="trainingsdagen-input" />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-zinc-500 text-xs font-bold uppercase tracking-wider">Duur (weken)</span>
              <input type="number" min="1" max="52" value={form.weken ?? 6}
                onChange={e => setForm(f => ({ ...f, weken: parseInt(e.target.value) || 1 }))}
                className="w-full bg-[#111] border border-white/8 rounded-xl px-4 py-3 text-white text-sm placeholder:text-zinc-600 focus:border-[#C9A84C]/50 focus:outline-none transition-colors" data-testid="weken-input" />
            </label>
            <label className="flex flex-col gap-1.5 sm:col-span-2">
              <span className="text-zinc-500 text-xs font-bold uppercase tracking-wider">Beschrijving</span>
              <textarea value={form.beschrijving} onChange={e => setForm(f => ({ ...f, beschrijving: e.target.value }))}
                placeholder="Korte omschrijving van het schema..." rows={2}
                className="w-full bg-[#111] text-white border border-white/10 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#C9A84C] placeholder-zinc-600 resize-none" />
            </label>
          </div>
        </div>

        {/* Day tabs */}
        {form.dagen.length > 0 && (
          <div className="bg-[#0f0f0f] border border-white/6 rounded-2xl overflow-hidden">
            {/* Day tabs nav */}
            <div className="flex border-b border-white/6 overflow-x-auto">
              {form.dagen.map((dag, i) => (
                <button key={i} onClick={() => setActiveDag(i)}
                  className={`px-4 py-3 text-xs font-bold whitespace-nowrap transition-colors ${activeDag === i ? "text-[#C9A84C] border-b-2 border-[#C9A84C] bg-[#C9A84C]/5" : "text-zinc-500 hover:text-white"}`}>
                  {dag.dag_naam}
                  {dag.oefeningen.length > 0 && <span className="ml-1.5 bg-[#C9A84C]/15 text-[#C9A84C] text-[10px] px-1.5 py-0.5 rounded-md">{dag.oefeningen.length}</span>}
                </button>
              ))}
            </div>
            <div className="p-5">
              {form.dagen[activeDag] && (
                <DagEditor dag={form.dagen[activeDag]} onChange={(updated) => updateDag(activeDag, updated)} token={token} />
              )}
            </div>
          </div>
        )}
      </div>

      {assignModal && schema?.id && (
        <AssignModal schemaId={schema.id} token={token} onClose={() => setAssignModal(false)} />
      )}
    </div>
  );
}

// ─── Main Schema Builder ──────────────────────────────────────────────────────
export default function SchemaBuilder({ adminToken }) {
  const [schemas, setSchemas] = useState([]);
  const [folders, setFolders] = useState([]);
  const [editing, setEditing] = useState(null); // null = list, "new" = new, {id,...} = edit
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [newFolderName, setNewFolderName] = useState("");
  const [showFolderInput, setShowFolderInput] = useState(false);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  // Use admin token from prop (passed by AdminPanel from localStorage)
  const token = adminToken || localStorage.getItem("fitsmith_admin_token") || "";
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    const [sRes, fRes] = await Promise.all([
      fetch(`${API}/api/admin/custom-schemas`, { headers }),
      fetch(`${API}/api/admin/schema-folders`, { headers })
    ]);
    const [s, f] = await Promise.all([sRes.json(), fRes.json()]);
    setSchemas(s.schemas || []);
    setFolders(f.folders || []);
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 2500); };

  const handleSave = async (form) => {
    if (editing === "new") {
      const res = await fetch(`${API}/api/admin/custom-schemas`, {
        method: "POST", headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(form)
      });
      const d = await res.json();
      showToast("Schema opgeslagen!");
      setEditing(null);
    } else {
      await fetch(`${API}/api/admin/custom-schemas/${editing.id}`, {
        method: "PUT", headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(form)
      });
      showToast("Schema bijgewerkt!");
      setEditing(null);
    }
    load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Schema verwijderen?")) return;
    await fetch(`${API}/api/admin/custom-schemas/${id}`, { method: "DELETE", headers });
    showToast("Schema verwijderd");
    setSchemas(p => p.filter(s => s.id !== id));
  };

  const handleDuplicate = async (id) => {
    await fetch(`${API}/api/admin/custom-schemas/${id}/duplicate`, { method: "POST", headers });
    showToast("Schema gedupliceerd!");
    load();
  };

  const handleDeleteFolder = async (folder) => {
    const count = schemas.filter(s => s.folder_id === folder.id).length;
    const msg = count > 0
      ? `Map "${folder.naam}" verwijderen? De ${count} schema('s) hierin worden niet verwijderd maar komen onder "Zonder map".`
      : `Map "${folder.naam}" verwijderen?`;
    if (!window.confirm(msg)) return;
    await fetch(`${API}/api/admin/schema-folders/${folder.id}`, { method: "DELETE", headers });
    if (selectedFolder === folder.id) setSelectedFolder(null);
    setFolders(p => p.filter(f => f.id !== folder.id));
    showToast("Map verwijderd");
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    await fetch(`${API}/api/admin/schema-folders`, {
      method: "POST", headers: { ...headers, "Content-Type": "application/json" },
      body: JSON.stringify({ naam: newFolderName, kleur: "#C9A84C" })
    });
    setNewFolderName("");
    setShowFolderInput(false);
    load();
  };

  const filteredSchemas = selectedFolder === null
    ? schemas
    : selectedFolder === "geen"
      ? schemas.filter(s => !s.folder_id)
      : schemas.filter(s => s.folder_id === selectedFolder);

  if (editing !== null) {
    return (
      <div className="h-full flex flex-col">
        <SchemaEditorForm
          schema={editing === "new" ? null : editing}
          folders={folders}
          onSave={handleSave}
          onCancel={() => setEditing(null)}
          token={token}
        />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="px-6 py-5 border-b border-white/6 flex items-center justify-between shrink-0">
        <div>
          <p className="text-zinc-500 text-xs uppercase tracking-widest font-bold mb-0.5">Admin</p>
          <h2 className="text-white font-bold text-lg">Schema Maker</h2>
        </div>
        <button onClick={() => setEditing("new")} data-testid="new-schema-btn"
          className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-[#C9A84C] to-[#D4AF37] text-black font-bold text-sm rounded-xl">
          <Plus size={15} /> Nieuw schema
        </button>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar — folders */}
        <div className="w-48 shrink-0 border-r border-white/6 flex flex-col overflow-y-auto">
          <div className="p-3 space-y-1">
            <button onClick={() => setSelectedFolder(null)}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold transition-colors ${selectedFolder === null ? "bg-[#C9A84C]/15 text-[#C9A84C]" : "text-zinc-400 hover:text-white hover:bg-white/4"}`}>
              <Folder size={13} /> Alle schema's <span className="ml-auto text-xs opacity-60">{schemas.length}</span>
            </button>
            <button onClick={() => setSelectedFolder("geen")}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold transition-colors ${selectedFolder === "geen" ? "bg-[#C9A84C]/15 text-[#C9A84C]" : "text-zinc-400 hover:text-white hover:bg-white/4"}`}>
              <Folder size={13} /> Zonder map <span className="ml-auto text-xs opacity-60">{schemas.filter(s => !s.folder_id).length}</span>
            </button>
            <div className="h-px bg-white/6 my-2" />
            {folders.map(f => (
              <div key={f.id} className="group relative flex items-center">
                <button onClick={() => setSelectedFolder(f.id)}
                  className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold transition-colors ${selectedFolder === f.id ? "bg-[#C9A84C]/15 text-[#C9A84C]" : "text-zinc-400 hover:text-white hover:bg-white/4"}`}>
                  <div className="w-2 h-2 rounded-full shrink-0" style={{ background: f.kleur }} />
                  <span className="truncate">{f.naam}</span>
                  <span className="ml-auto text-xs opacity-60 pr-5">{schemas.filter(s => s.folder_id === f.id).length}</span>
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); handleDeleteFolder(f); }}
                  data-testid={`delete-folder-btn-${f.id}`}
                  title="Map verwijderen"
                  className="absolute right-2 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded-lg hover:bg-red-500/15 text-zinc-600 hover:text-red-400"
                >
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>
          {/* New folder */}
          <div className="mt-auto p-3">
            {showFolderInput ? (
              <div className="space-y-2">
                <input autoFocus value={newFolderName} onChange={e => setNewFolderName(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") handleCreateFolder(); if (e.key === "Escape") setShowFolderInput(false); }}
                  placeholder="Mapnaam..." className="builder-input text-xs" />
                <div className="flex gap-1">
                  <button onClick={handleCreateFolder} className="flex-1 py-1.5 bg-[#C9A84C] text-black text-xs font-bold rounded-lg">Maken</button>
                  <button onClick={() => setShowFolderInput(false)} className="px-2 text-zinc-500 hover:text-white"><X size={12} /></button>
                </div>
              </div>
            ) : (
              <button onClick={() => setShowFolderInput(true)} data-testid="new-folder-btn"
                className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold text-zinc-600 hover:text-zinc-300 hover:bg-white/4 transition-colors">
                <FolderPlus size={12} /> Nieuwe map
              </button>
            )}
          </div>
        </div>

        {/* Schema list */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="text-zinc-600 text-sm text-center py-12">Laden...</div>
          ) : filteredSchemas.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 rounded-2xl bg-[#1a1a1a] flex items-center justify-center mx-auto mb-4">
                <Plus size={24} className="text-zinc-600" />
              </div>
              <p className="text-white font-bold text-sm mb-1">Nog geen schema's</p>
              <p className="text-zinc-500 text-xs mb-4">Maak je eerste schema aan</p>
              <button onClick={() => setEditing("new")} className="px-4 py-2 bg-[#C9A84C] text-black font-bold text-xs rounded-xl">
                Schema maken
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {filteredSchemas.map(s => {
                const folder = folders.find(f => f.id === s.folder_id);
                return (
                  <div key={s.id} className="bg-[#0f0f0f] border border-white/6 rounded-2xl p-5 flex flex-col gap-3 hover:border-[#C9A84C]/30 transition-colors group">
                    {/* Top */}
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          {folder && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: folder.kleur + "22", color: folder.kleur }}>{folder.naam}</span>}
                        </div>
                        <h3 className="text-white font-bold text-sm truncate">{s.naam}</h3>
                        <p className="text-zinc-500 text-xs mt-0.5">{s.doel} · {s.niveau} · {s.trainingsdagen}x/week</p>
                      </div>
                    </div>
                    {s.beschrijving && <p className="text-zinc-600 text-xs line-clamp-2">{s.beschrijving}</p>}
                    {/* Stats */}
                    <div className="flex gap-3 text-xs text-zinc-600">
                      <span>{s.dagen?.length || 0} dagen</span>
                      <span>{s.dagen?.reduce((a, d) => a + (d.oefeningen?.length || 0), 0)} oefeningen</span>
                    </div>
                    {/* Actions */}
                    <div className="flex gap-1.5 mt-auto pt-3 border-t border-white/5">
                      <button onClick={() => setEditing(s)} data-testid={`edit-schema-${s.id}`}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl bg-[#1a1a1a] hover:bg-[#C9A84C]/10 text-zinc-400 hover:text-[#C9A84C] text-xs font-bold transition-colors">
                        <Edit2 size={12} /> Bewerken
                      </button>
                      <button onClick={() => handleDuplicate(s.id)}
                        className="w-8 h-8 rounded-xl bg-[#1a1a1a] flex items-center justify-center text-zinc-500 hover:text-white transition-colors" title="Dupliceren">
                        <Copy size={12} />
                      </button>
                      <button onClick={() => handleDelete(s.id)}
                        className="w-8 h-8 rounded-xl bg-[#1a1a1a] flex items-center justify-center text-zinc-500 hover:text-red-400 transition-colors" title="Verwijderen">
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-[#111] border border-[#C9A84C]/30 text-[#C9A84C] text-sm font-semibold px-5 py-3 rounded-xl shadow-2xl flex items-center gap-2 animate-fadeIn">
          <Check size={14} /> {toast}
        </div>
      )}
    </div>
  );
}
